import { TypographyStylesProvider, Text, type TextProps } from '@mantine/core';

/**
 * Strips HTML tags from a string and collapses whitespace. Used for previews
 * (cards, list summaries) where we want a plain-text excerpt clamp.
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Renders an HTML description block (course / session / KB summary) using
 * Mantine's <c>TypographyStylesProvider</c> so authored markup picks up
 * theme-aware typography (light + dark) without any extra CSS.
 *
 * Falls back to a dimmed paragraph if the value is empty/whitespace.
 */
export function RichText({
  html,
  fallback,
  className,
  style,
}: {
  html: string | null | undefined;
  fallback?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const trimmed = (html ?? '').trim();
  if (!trimmed) {
    return fallback ? (
      <Text c="dimmed" fs="italic" className={className} style={style}>
        {fallback}
      </Text>
    ) : null;
  }
  return (
    <TypographyStylesProvider className={className} style={{ marginBlock: 0, ...style }}>
      <div dangerouslySetInnerHTML={{ __html: trimmed }} />
    </TypographyStylesProvider>
  );
}

/**
 * Plain-text preview of an HTML description, suitable for card / list usage.
 * Accepts the same <c>Text</c> props as Mantine so callers can keep
 * <c>lineClamp</c>, <c>size</c>, etc. without changes.
 */
export function RichTextPreview({
  html,
  fallback,
  ...textProps
}: { html: string | null | undefined; fallback?: string } & TextProps) {
  const text = stripHtml(html) || fallback || '';
  if (!text) return null;
  return <Text {...textProps}>{text}</Text>;
}
